export interface BrowserFingerprint {
  platform: string | null;
  userAgent: string | null;
  hardwareConcurrency: number | null;
  screenResolution: string | null;
  timezone: string | null;
  language: string | null;
  canvasFingerprint: string | null;
  webglFingerprint: string | null;
  fonts: string[];
}

export async function getBrowserFingerprint(): Promise<BrowserFingerprint> {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    throw new Error("Browser fingerprint can only be generated in the browser");
  }

  return {
    platform: navigator.platform || null,
    userAgent: navigator.userAgent || null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    screenResolution: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    language: navigator.language || null,
    canvasFingerprint: getCanvasFingerprint(),
    webglFingerprint: getWebglFingerprint(),
    fonts: detectFonts(),
  };
}

function getCanvasFingerprint(): string | null {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    canvas.width = 220;
    canvas.height = 30;

    ctx.textBaseline = "top";
    ctx.font = "14px Arial";

    ctx.fillStyle = "#f60";
    ctx.fillRect(0, 0, 100, 20);

    ctx.fillStyle = "#069";
    ctx.fillText("fingerprint-check 🔒", 2, 2);

    return hashString(canvas.toDataURL());
  } catch (error) {
    return null;
  }
}

function getWebglFingerprint(): string | null {
  try {
    const canvas = document.createElement("canvas");

    const gl =
      (canvas.getContext("webgl") ||
        canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;

    if (!gl) {
      return null;
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");

    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);

    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);

    return hashString(`${vendor}~${renderer}`);
  } catch (error) {
    return null;
  }
}

function detectFonts(): string[] {
  const testFonts = [
    "Arial",
    "Courier New",
    "Georgia",
    "Times New Roman",
    "Verdana",
    "Comic Sans MS",
  ];

  const baseFonts = [
    "monospace",
    "sans-serif",
    "serif",
  ];

  const testString = "mmmmmmmmmmlli";
  const testSize = "72px";

  const span = document.createElement("span");

  span.style.fontSize = testSize;
  span.style.position = "absolute";
  span.style.left = "-9999px";
  span.innerHTML = testString;

  document.body.appendChild(span);

  const baseWidths: Record<string, number> = {};

  baseFonts.forEach((base) => {
    span.style.fontFamily = base;
    baseWidths[base] = span.offsetWidth;
  });

  const available = testFonts.filter((font) => {
    return baseFonts.some((base) => {
      span.style.fontFamily = `'${font}', ${base}`;
      return span.offsetWidth !== baseWidths[base];
    });
  });

  document.body.removeChild(span);

  return available.sort();
}

function hashString(value: string): string {
  let hash = 0;

  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }

  return hash.toString(16);
}